import Config from '../config/Config';

// Per-member purchase limit of a voucher.
// New API (perMemberLimit + limitPeriod) wins; legacy API only sends maxPerMonth (always a rolling 30 days).
export const getVoucherLimit = voucher => {
	if (!voucher) {
		return { limit: null, period: 'rolling_30d' };
	}

	if (voucher.perMemberLimit >= 1) {
		return { limit: Number(voucher.perMemberLimit), period: voucher.limitPeriod || 'rolling_30d' };
	}

	if (voucher.maxPerMonth >= 1) {
		return { limit: Number(voucher.maxPerMonth), period: 'rolling_30d' };
	}

	return { limit: null, period: 'rolling_30d' };
};

// Per-member purchase limit of a voucher package.
// New API (perMemberLimit + limitPeriod) wins; legacy API only sends limitNumber + monthlyLimit.
// limit is null when the package is unlimited.
export const getPackageLimit = pkg => {
	if (!pkg) {
		return { limit: null, period: 'lifetime' };
	}

	if (pkg.perMemberLimit >= 1) {
		return { limit: Number(pkg.perMemberLimit), period: pkg.limitPeriod || 'rolling_30d' };
	}

	if (pkg.limitNumber >= 1) {
		return { limit: Number(pkg.limitNumber), period: pkg.monthlyLimit ? 'rolling_30d' : 'lifetime' };
	}

	return { limit: null, period: pkg.limitPeriod || 'lifetime' };
};

// How many of the package the member has already bought (or has reserved in unpaid orders).
// GET /api/voucher-package/limits/:id returned a plain number on older backends and a status object
// ({ purchaseCount, remaining, ... }) on newer ones.
export const getPackageBought = status => {
	if (status && typeof status === 'object') {
		return Number(status.purchaseCount) || 0;
	}

	return Number(status) || 0;
};

// How many more packages the member may add to the cart (capped at the per-order maximum, so an unlimited
// package never produces an infinite number).
export const getPackageRemaining = (pkg, bought = 0) => {
	const { limit } = getPackageLimit(pkg);

	if (limit === null) {
		return Config.MAX_CART_ITEMS_ADD;
	}

	return Math.max(Math.min(limit - (Number(bought) || 0), Config.MAX_CART_ITEMS_ADD), 0);
};

// How many more units of the voucher the member may put in the cart.
// status is the optional response of GET /api/voucher/limits/:id (only logged in members, new API only).
export const getVoucherMaxQty = (voucher, status, inCart = 0) => {
	let max = Math.min(voucher.stockAvailable, Config.MAX_CART_ITEMS_ADD);
	const { limit } = getVoucherLimit(voucher);

	if (status && status.limit !== null && status.limit !== undefined && status.remaining !== null && status.remaining !== undefined) {
		max = Math.min(max, status.remaining - inCart);
	} else if (limit !== null) {
		max = Math.min(max, limit - inCart);
	}

	return Math.max(max, 0);
};

// i18n codes come from the API; use an English fallback until the translation has been added there.
export const translateWithFallback = (translateMessage, code, fallback, fields = {}) => {
	const message = translateMessage(code, fields);

	if (message !== code) {
		return message;
	}

	return Object.keys(fields).reduce((a, f) => a.replace(new RegExp('{' + f + '}', 'g'), fields[f]), fallback);
};

export const getLimitPeriodText = (period, translateMessage) => {
	const texts = {
		rolling_30d: ['page.voucher.text.limitPeriodRolling', 'per 30 days'],
		calendar_month: ['page.voucher.text.limitPeriodMonth', 'per calendar month'],
		lifetime: ['page.voucher.text.limitPeriodLifetime', 'in total']
	};
	const [code, fallback] = texts[period] || texts.rolling_30d;

	return translateWithFallback(translateMessage, code, fallback);
};
